import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Input,
    Label,
    ListBox,
    Modal,
    Select,
    TextField,
} from '@heroui/react';
import { DatePickerField } from '@/components/datePickerField';
import {
    SUPPLIER_PAYMENT_METHODS,
    type SupplierPaymentMethod,
} from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { formatAmount, paymentMethodLabelKey } from '../suppliersMeta';
import type {
    SupplierPaymentInvoiceOption,
    useSupplierPaymentForm,
} from './useSupplierPaymentForm';

type SupplierPaymentModalViewProps = {
    error: string | null;
    form: ReturnType<typeof useSupplierPaymentForm>;
    invoices: SupplierPaymentInvoiceOption[];
    isOpen: boolean;
    isSubmitting: boolean;
    onClose: () => void;
    supplierName: string;
    /** Latest selectable date — the payment cannot be in the future. */
    today: string;
};

/** Sentinel key for "no invoice", since a ListBox key cannot be null. */
const UNALLOCATED = '__unallocated__';

export const SupplierPaymentModalView: React.FC<
    SupplierPaymentModalViewProps
> = ({
    error,
    form,
    invoices,
    isOpen,
    isSubmitting,
    onClose,
    supplierName,
    today,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('suppliers.components.supplierPaymentModal.title'),
            description: t(
                'suppliers.components.supplierPaymentModal.description',
                { supplier: supplierName },
            ),
            dateLabel: t('suppliers.components.supplierPaymentModal.dateLabel'),
            amountLabel: t(
                'suppliers.components.supplierPaymentModal.amountLabel',
            ),
            amountPlaceholder: t(
                'suppliers.components.supplierPaymentModal.amountPlaceholder',
            ),
            methodLabel: t(
                'suppliers.components.supplierPaymentModal.methodLabel',
            ),
            invoiceLabel: t(
                'suppliers.components.supplierPaymentModal.invoiceLabel',
            ),
            invoiceUnallocated: t(
                'suppliers.components.supplierPaymentModal.invoiceUnallocated',
            ),
            invoiceHint: t(
                'suppliers.components.supplierPaymentModal.invoiceHint',
            ),
            referenceLabel: t(
                'suppliers.components.supplierPaymentModal.referenceLabel',
            ),
            referencePlaceholder: t(
                'suppliers.components.supplierPaymentModal.referencePlaceholder',
            ),
            noteLabel: t('suppliers.components.supplierPaymentModal.noteLabel'),
            notePlaceholder: t(
                'suppliers.components.supplierPaymentModal.notePlaceholder',
            ),
            cancel: t('suppliers.components.supplierPaymentModal.cancel'),
            submit: t('suppliers.components.supplierPaymentModal.submit'),
            amountErrors: {
                invalid: t(
                    'suppliers.components.supplierPaymentModal.amountErrors.invalid',
                ),
                subTetri: t(
                    'suppliers.components.supplierPaymentModal.amountErrors.subTetri',
                ),
                notPositive: t(
                    'suppliers.components.supplierPaymentModal.amountErrors.notPositive',
                ),
            },
        }),
        [supplierName, t],
    );

    return (
        <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal.Backdrop>
                <Modal.Container className="max-sm:p-0" size="md">
                    <Modal.Dialog
                        className="max-sm:m-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:w-full max-sm:max-w-none max-sm:rounded-none max-sm:p-4"
                        {...getNodeTestIdProp('supplier-payment-form')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header
                            {...getNodeTestIdProp('supplier-payment-header')}
                        >
                            <Modal.Heading
                                {...getNodeTestIdProp('supplier-payment-title')}
                            >
                                {copy.title}
                            </Modal.Heading>
                            <p
                                className="text-sm text-muted"
                                {...getNodeTestIdProp(
                                    'supplier-payment-description',
                                )}
                            >
                                {copy.description}
                            </p>
                        </Modal.Header>
                        <Modal.Body
                            className="flex flex-col gap-5"
                            {...getNodeTestIdProp('supplier-payment-body')}
                        >
                            <DatePickerField
                                id="supplier-payment-date"
                                isRequired
                                label={copy.dateLabel}
                                maxValue={today}
                                name="date"
                                onChange={form.onDateChange}
                                value={form.date}
                            />
                            <TextField
                                isRequired
                                onChange={form.onAmountChange}
                                value={form.amount}
                                {...getNodeTestIdProp(
                                    'supplier-payment-amount',
                                )}
                            >
                                <Label>{copy.amountLabel}</Label>
                                <Input
                                    inputMode="decimal"
                                    onBlur={form.onAmountBlur}
                                    placeholder={copy.amountPlaceholder}
                                />
                            </TextField>
                            {form.showAmountError && form.amountError ? (
                                <p
                                    className="-mt-3 text-sm text-danger"
                                    {...getNodeTestIdProp(
                                        'supplier-payment-amount-error',
                                    )}
                                >
                                    {copy.amountErrors[form.amountError]}
                                </p>
                            ) : null}
                            <Select
                                onChange={(next) =>
                                    next &&
                                    form.onMethodChange(
                                        String(next) as SupplierPaymentMethod,
                                    )
                                }
                                value={form.method}
                            >
                                <Label>{copy.methodLabel}</Label>
                                <Select.Trigger
                                    {...getNodeTestIdProp(
                                        'supplier-payment-method',
                                    )}
                                >
                                    <Select.Value />
                                    <Select.Indicator />
                                </Select.Trigger>
                                <Select.Popover>
                                    <ListBox>
                                        {SUPPLIER_PAYMENT_METHODS.map(
                                            (method) => (
                                                <ListBox.Item
                                                    id={method}
                                                    key={method}
                                                    textValue={t(
                                                        paymentMethodLabelKey(
                                                            method,
                                                        ),
                                                    )}
                                                >
                                                    {t(
                                                        paymentMethodLabelKey(
                                                            method,
                                                        ),
                                                    )}
                                                    <ListBox.ItemIndicator />
                                                </ListBox.Item>
                                            ),
                                        )}
                                    </ListBox>
                                </Select.Popover>
                            </Select>
                            <div className="flex flex-col gap-1.5">
                                <Select
                                    onChange={(next) =>
                                        form.onInvoiceChange(
                                            !next ||
                                                String(next) === UNALLOCATED
                                                ? null
                                                : String(next),
                                        )
                                    }
                                    value={form.invoiceId ?? UNALLOCATED}
                                >
                                    <Label>{copy.invoiceLabel}</Label>
                                    <Select.Trigger
                                        {...getNodeTestIdProp(
                                            'supplier-payment-invoice',
                                        )}
                                    >
                                        <Select.Value />
                                        <Select.Indicator />
                                    </Select.Trigger>
                                    <Select.Popover>
                                        <ListBox>
                                            <ListBox.Item
                                                id={UNALLOCATED}
                                                textValue={
                                                    copy.invoiceUnallocated
                                                }
                                            >
                                                {copy.invoiceUnallocated}
                                                <ListBox.ItemIndicator />
                                            </ListBox.Item>
                                            {invoices.map((invoice) => (
                                                <ListBox.Item
                                                    id={invoice.id}
                                                    key={invoice.id}
                                                    textValue={`${invoice.invoiceNumber} · ${formatAmount(invoice.outstandingTetri)}`}
                                                >
                                                    {`${invoice.invoiceNumber} · ${formatAmount(invoice.outstandingTetri)}`}
                                                    <ListBox.ItemIndicator />
                                                </ListBox.Item>
                                            ))}
                                        </ListBox>
                                    </Select.Popover>
                                </Select>
                                <p className="text-xs text-muted">
                                    {copy.invoiceHint}
                                </p>
                            </div>
                            <TextField
                                onChange={form.onReferenceChange}
                                value={form.reference}
                                {...getNodeTestIdProp(
                                    'supplier-payment-reference',
                                )}
                            >
                                <Label>{copy.referenceLabel}</Label>
                                <Input
                                    placeholder={copy.referencePlaceholder}
                                />
                            </TextField>
                            <TextField
                                onChange={form.onNoteChange}
                                value={form.note}
                                {...getNodeTestIdProp('supplier-payment-note')}
                            >
                                <Label>{copy.noteLabel}</Label>
                                <Input placeholder={copy.notePlaceholder} />
                            </TextField>
                            {error ? (
                                <p
                                    className="text-sm text-danger"
                                    {...getNodeTestIdProp(
                                        'supplier-payment-error',
                                    )}
                                >
                                    {error}
                                </p>
                            ) : null}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onPress={onClose}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'supplier-payment-cancel',
                                )}
                            >
                                {copy.cancel}
                            </Button>
                            <Button
                                isDisabled={!form.isValid || isSubmitting}
                                onPress={form.onSubmit}
                                variant="primary"
                                {...getNodeTestIdProp(
                                    'supplier-payment-submit',
                                )}
                            >
                                {copy.submit}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
